// ./src/config/eventFeatureNav.ts
//
// THE EVENT MENU SHOWS WHAT THE EVENT USES (ROADMAP A2b #63). Each event page in `siteMap.ts` is tagged with the event
// feature it belongs to (`eventFeature: 'budget'`); this decides, per event, which tagged pages sit in the menu as usual
// and which move behind a "Show more".
//
// ⚠⚠ NOTHING IS HIDDEN. A page the event does not use moves behind "Show more" — one tap away, never gone — and a page
// that HAS DATA always stays in the menu whatever the feature switch says. A host who used the budget and then sees
// Budget vanish reads it as "my data is gone" (the A2 downgrade lesson), not as a tidier menu.
//
// The rule, per tagged page:
//   - the event has NO feature record (`settings.features` absent) → in the menu. Every event made before #62 has none,
//     and must keep its whole menu (owner, 2026-09-29).
//   - the feature is ON → in the menu.
//   - the feature has data, OR nobody could tell (usage still loading, the read failed, file storage unreachable) → in
//     the menu. "Could not tell" is never read as "no data".
//   - otherwise → behind "Show more".
// And per section: when EVERY visible page of a section with no page of its own is behind "Show more", the SECTION
// moves behind the menu's own "Show more" instead — a birthday party should not carry an Inventory heading holding
// nothing but a "Show more" row. Revealed, such a section shows all of its pages.
//
// ⚠ Premium is SHOWN, never hidden (owner): a page whose plan feature the account lacks carries `navPremium` wherever it
// sits. Opening one shows the page's existing upgrade screen (`withPlanGate`).
//
// ⚠ Pure and TYPE-ONLY in its imports, so `siteMap.ts` can use it at runtime and `npm run check-nav` can run it on the
// real sitemap without dragging the app in behind it (see scripts/navLoadSiteMap.ts).
import type { SiteMapItem } from './siteMap';

/** What the menu knows about the current event's features. */
export interface EventFeatureNavCtx {
  /** `settings.features.on` — null when the event has NO record, which means everything is on. */
  on: readonly string[] | null;
  /**
   * `eventFeatureUsage` — null while it loads or when the read failed. ⚠ Null is "unknown", and an unknown page is kept
   * in the menu: the fallback is the menu every event had before #63, never a page tucked away with rows behind it.
   */
  usage: { withData: readonly string[]; unknown: readonly string[] } | null;
  /** Whether the account's plan lacks a PLAN feature (→ the premium mark). Omitted → no marks (a crew session). */
  isLocked?: (planFeatureCode: string) => boolean;
}

/** The feature has rows on this event — or might: unknown counts as yes. */
export const featureMayHaveData = (ctx: EventFeatureNavCtx | null | undefined, code: string): boolean => {
  if (!ctx?.usage) {
    return true;
  }

  return ctx.usage.withData.includes(code) || ctx.usage.unknown.includes(code);
};

/**
 * The feature is KNOWN to have (or may have) rows — for a hide that predates #63 (the ticketed-only pages), where a
 * still-loading answer must keep today's behaviour rather than flash four selling pages onto every RSVP event.
 */
export const featureKnownToHaveData = (ctx: EventFeatureNavCtx | null | undefined, code: string): boolean =>
  !!ctx?.usage && featureMayHaveData(ctx, code);

/** Does a page tagged `code` belong in the menu itself (true), or behind "Show more" (false)? */
export const isFeatureInMenu = (ctx: EventFeatureNavCtx | null | undefined, code: string | undefined): boolean => {
  if (!code || !ctx || ctx.on === null) {
    return true;
  }

  return ctx.on.includes(code) || featureMayHaveData(ctx, code);
};

const isVisible = (item: SiteMapItem): boolean => item.visible !== false;

const withPremium = (item: SiteMapItem, ctx: EventFeatureNavCtx): SiteMapItem => {
  const premium = !!item.featureCode && !!ctx.isLocked?.(item.featureCode);
  const items = Array.isArray(item.items) ? item.items.map((child) => withPremium(child, ctx)) : item.items;

  return { ...item, navPremium: premium, items };
};

/**
 * Mark the event nav: `navMore` on what sits behind "Show more" (a page inside its section, or a whole top-level
 * section/page behind the menu's own), `navPremium` on what the plan does not include. Items are copied, never mutated.
 * With no ctx (the account workspace) the tree comes back unchanged.
 */
export const markEventFeatureNav = (items: SiteMapItem[], ctx: EventFeatureNavCtx | null | undefined): SiteMapItem[] => {
  if (!ctx) {
    return items;
  }

  return items.map((top) => {
    const item = withPremium(top, ctx);
    const children = Array.isArray(item.items) ? item.items : [];
    const visibleChildren = children.filter(isVisible);

    if (visibleChildren.length === 0) {
      // A top-level page of its own (Policies & compliance).
      return { ...item, navMore: !isFeatureInMenu(ctx, item.eventFeature) };
    }

    const marked = children.map((child) =>
      isVisible(child) && !isFeatureInMenu(ctx, child.eventFeature) ? { ...child, navMore: true } : { ...child, navMore: false },
    );
    const allMore = marked.filter(isVisible).every((child) => child.navMore);

    // ⚠ Only a section with NO page of its own moves as a whole. Analytics has a landing page; moving it would take
    // an always-on page behind "Show more" with its reports.
    if (allMore && !item.url) {
      return { ...item, navMore: true, items: marked.map((child) => ({ ...child, navMore: false })) };
    }

    return { ...item, navMore: false, items: marked };
  });
};

/** A group's visible children, split into what the menu shows and what sits behind its "Show more". */
export const splitShowMore = (items: readonly SiteMapItem[]): { shown: SiteMapItem[]; more: SiteMapItem[] } => ({
  shown: items.filter((item) => isVisible(item) && !item.navMore),
  more: items.filter((item) => isVisible(item) && !!item.navMore),
});

/** How closely `item` (its own url or any descendant's) contains `path`: the matching url's length, or -1. */
const matchLength = (item: SiteMapItem, path: string): number => {
  const own = item.url && (path === item.url || path.startsWith(`${item.url}/`)) ? item.url.length : -1;

  return Math.max(own, ...(item.items ?? []).map((child) => matchLength(child, path)));
};

/**
 * Which of `items` the host is on — its page, a drawer stage, or a page inside it.
 *
 * ⚠ LONGEST MATCH, NOT "ANY PREFIX". Tickets & Pricing is `…/registration`, a plain prefix of `…/registration/questions`,
 * and the Dashboard is `/events/:id`, a prefix of every page of the event: "does it contain the path" answers yes for
 * the wrong item, and a "Show more" would open itself on a page that is not behind it.
 */
export const activeNavItem = (items: readonly SiteMapItem[], path: string): SiteMapItem | null => {
  let best: { item: SiteMapItem; length: number } | null = null;

  for (const item of items) {
    const length = matchLength(item, path);

    if (length >= 0 && (!best || length > best.length)) {
      best = { item, length };
    }
  }

  return best?.item ?? null;
};
